import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ModalDialogueService } from '../../../shared/components/modal-dialogue/modal-dialogue.service';
import { DataCheckService } from '../../../shared/services/data-check.service';
import { LoadingSpinnerService } from '../../../shared/services/loading-spinner.service';
import { ToasterService } from '../../../shared/services/toaster.service';
import { JobsService } from '../jobs/jobs.service';
import { RunSheetService } from './runsheet.service';

@Component({
  selector: 'gtapp-runsheet',
  templateUrl: './runsheet.component.html',
  styleUrl: './runsheet.component.scss',
  standalone: false,
})
export class RunSheetComponent implements OnInit, OnDestroy {
  runSheetRows: number = 15;
  runSheetNextCursor: string = '';

  runSheetDataList: any = [];

  alreadyLoading: boolean = false;

  deleteOptionSelected: number = 1;

  runsheetTabs: any = {};
  selectedTab: string = '';

  runSheetSubject = new Subject<void>();

  addEditJob =
    this.dataCheckService.isUserAdmin() ||
    this.dataCheckService.isDispatchUser({
      permission: 'job_type',
      action: 'add',
      type_identity: 6,
    });

  constructor(
    private runSheetService: RunSheetService,
    private dialogService: ModalDialogueService,
    private spinnerService: LoadingSpinnerService,
    private toasterService: ToasterService,

    private router: Router,
    private dataCheckService: DataCheckService,
    private viewContainerRef: ViewContainerRef,
    private jobService: JobsService,
  ) {}
  ngOnDestroy(): void {
    if (this.runSheetSubject) {
      this.runSheetSubject?.next();
      this.runSheetSubject?.complete();
    }
  }
  async ngOnInit(): Promise<void> {
    this.spinnerService.show();
    this.runsheetTabs = {
      tab10: 'Scheduled Patrols',
    };
    const finishedPatrols = await this.checkRunsheetCompleted();
    if (finishedPatrols) this.runsheetTabs['tab11'] = 'Finished';
    const searchPresent = await this.checkScheduledPatrolJobs();
    if (searchPresent) this.runsheetTabs['tab12'] = 'Search';
    const savedTab =
      sessionStorage.getItem('selectedTab') || this.runsheetTabs.tab10;

    this.changeTab({ value: savedTab }, false);
  }
  changeTab(tabData: any, manualSelect = true) {
    this.selectedTab = tabData?.value;
    this.runSheetDataList = [];
    this.runSheetNextCursor = '';
    this.runSheetSubject?.next();
    this.alreadyLoading = false;
    sessionStorage.setItem('selectedTab', this.selectedTab);
    if (!Object.values(this.runsheetTabs).includes(this.selectedTab)) {
      this.selectedTab = this.runsheetTabs.tab10;
    }

    if (
      [this.runsheetTabs.tab10, this.runsheetTabs.tab11].includes(
        this.selectedTab,
      )
    ) {
      this.getAllRunSheets();
    } else {
      if (manualSelect) sessionStorage.removeItem(btoa('spSearchFilters'));
      this.spinnerService.hide();
    }
  }
  checkScheduledPatrolJobs() {
    return new Promise((resolve) => {
      this.jobService
        .fetchJobDetails({ rows: 1, runsheet: 1 }, { items_exists_check: 1 })
        .subscribe((response: any) => {
          resolve(response?.data);
        });
    });
  }
  checkRunsheetCompleted() {
    return new Promise((resolve) => {
      this.runSheetService
        .getRunSheets({
          completed_runsheet: 1,
          exists_check: 1,
        })
        .subscribe((response: any) => {
          resolve(response?.data);
        });
    });
  }
  getAllRunSheets(showSpiner = true) {
    if (showSpiner) this.spinnerService.show();
    let params: any = {};
    if (this.runSheetRows) {
      params['rows'] = this.runSheetRows;
    }
    if (this.runSheetNextCursor) {
      params['next_cursor'] = this.runSheetNextCursor;
    }
    if (this.selectedTab && this.selectedTab == this.runsheetTabs?.tab11) {
      params.completed_runsheet = 1;
    }
    this.runSheetService
      .getRunSheets(params)
      .pipe(takeUntil(this.runSheetSubject))
      .subscribe((response: any) => {
        if (response['status'] == 'success') {
          const responseData = response?.data;

          const existingIds = new Set(
            this.runSheetDataList?.map((item: any) => item?.id),
          );
          const filteredData = responseData?.filter(
            (item: any) => !existingIds?.has(item?.id),
          );
          this.runSheetDataList = [...this.runSheetDataList, ...filteredData];

          this.alreadyLoading = false;
          this.runSheetNextCursor = response?.next_cursor;
          setTimeout(() => {
            this.checkRemainingSpace();
          }, 1000);

          this.spinnerService.hide();
        } else {
          this.spinnerService.hide();
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response['message'],
          });
        }
      });
  }
  resetValues() {
    this.runSheetDataList = [];
    this.runSheetNextCursor = '';
  }
  duplicateRunSheets(runSheetData: any) {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: 'Are you sure?',
        message: `Press confirm to duplicate runsheet.`,
      },
    });
    dialogRef.afterClosed().subscribe((value) => {
      if (value === true) {
        this.router.navigate([
          '/add-runsheet',
          { rKey: String(runSheetData?.id) },
        ]);
      }
    });
  }
  openRunSheetDetailPage(runSheetData: any) {
    this.router.navigate([
      '/view-runsheet',
      { rKey: String(runSheetData?.id) },
    ]);
  }
  loadMoreData() {
    if (this.alreadyLoading || !this.runSheetNextCursor) return;
    this.alreadyLoading = true;
    this.getAllRunSheets(false);
  }

  checkRemainingSpace() {
    const contentContainer = document.querySelector('.runsheet-content');
    const contentHeight: any = contentContainer?.getBoundingClientRect().bottom;
    const viewportHeight = window.innerHeight; //

    if (contentHeight < viewportHeight && !this.alreadyLoading) {
      // If content height is less than the viewport, load more items
      this.loadMoreData();
    }
  }
  @HostListener('window:scroll', ['$event'])
  onScroll(event: any): void {
    const scrollPosition = event.target.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = event.target.documentElement.scrollHeight;

    if (
      scrollPosition + windowHeight >= documentHeight - 5 &&
      !this.alreadyLoading
    ) {
      this.loadMoreData();
    }
  }
  openDeleteRunSheetTemplate(runSheetData: any, template: any) {
    const dialogRef = this.dialogService.open(
      template,
      {
        data: runSheetData,
      },
      this.viewContainerRef,
    );
    dialogRef?.afterClosed()?.subscribe((res: any) => {
      if (res?.id) {
        if (res?.repeat_type == 'once') this.deleteOptionSelected = 1;
        let params: any = { delete_run_sheet: 1 };
        if (this.deleteOptionSelected == 2) params.delete_shift = 1;
        this.runSheetService
          .updateRunSheet(res?.id, { event_date: res?.event_date }, params)
          .then((response: any) => {
            if (response?.status === 'success') {
              this.resetValues();
              this.getAllRunSheets();
            } else {
              this.spinnerService.hide();
              this.toasterService.setMessage({
                successMessage: '',
                errorMessage: response?.message,
              });
            }
          });
      }
    });
  }
}
